import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Autocomplete as MuiAutocomplete, useTheme } from "@mui/material";
import { memo, SyntheticEvent, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Divider } from "../Divider";
import {
  type ShopSelectionItem,
  type ShopSelectionContextValue,
  useShopSelection
} from "../ShopSelection";
import { makePaperStyles, Switcher } from "./ShopSwitch.styles";

export const ShopPicker = ({
  selectedShop,
  shops,
  loading,
  requestShopChange,
  refetchShops
}: ShopSelectionContextValue["state"] & ShopSelectionContextValue["actions"]) => {
  const theme = useTheme();
  const [opened, setOpened] = useState(false);
  const { t } = useTranslation();

  const handleOpen = useCallback(() => {
    setOpened(true);
    void refetchShops();
  }, [refetchShops]);

  const handleClose = useCallback(() => {
    setOpened(false);
  }, []);

  const handleChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, nextShop: ShopSelectionItem) => {
      requestShopChange(nextShop.shopId);
    },
    [requestShopChange]
  );

  if (loading || shops.length <= 1) {
    return null;
  }

  return (
    <>
      <MuiAutocomplete
        options={shops}
        value={selectedShop}
        open={opened}
        loading={loading}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleChange}
        getOptionLabel={({ shopId, name }) => `${shopId} - ${name}`}
        isOptionEqualToValue={(option, selected) => option.shopId === selected.shopId}
        renderInput={(params) => (
          <Switcher
            {...params}
            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
            label={t("navigation.accountMenu.shopSwitcherLabel")}
          />
        )}
        popupIcon={<UnfoldMoreIcon />}
        disableClearable
        slotProps={{
          paper: {
            sx: makePaperStyles(theme)
          }
        }}
      />
      <Divider />
    </>
  );
};

const ShopSwitchComponent = () => {
  const { state, actions } = useShopSelection();
  return <ShopPicker {...state} {...actions} />;
};

export const ShopSwitch = memo(ShopSwitchComponent);
